import { useEffect, useState } from "react";
import { Link2, X, Search, Check, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { UnitPills } from "./UnitPills";
import { euroPrecise } from "../lib/constants";

/**
 * Pulsante "Associa a fornitore" per gli ingredienti.
 * Apre un dialog che mostra tutti i fornitori del portale dell'utente,
 * permette di selezionarne uno, impostare prezzo + UM (precompilati con
 * cost_per_unit + unit dell'ingrediente), e crea/aggiorna la voce
 * `supplier_prices`.
 */
export function AssociateIngredientToSupplierButton({ ingredient, compact = false, onChanged }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button
                type="button"
                data-testid={`assoc-supplier-ing-btn-${ingredient.id}`}
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setOpen(true);
                }}
                title="Associa questo ingrediente a un fornitore"
                className={
                    compact
                        ? "inline-flex items-center justify-center h-9 w-9 rounded-full bg-brand-navy/8 text-brand-navy hover:bg-brand-navy hover:text-white transition shrink-0"
                        : "inline-flex items-center gap-1.5 rounded-full bg-brand-navy text-white text-[12px] font-bold px-3 py-1.5 hover:bg-brand-navyLight transition"
                }
            >
                <Link2 size={compact ? 15 : 13} strokeWidth={compact ? 2.2 : 2.4} />
                {!compact && <span>Associa fornitore</span>}
            </button>
            {open && (
                <AssociateIngredientDialog
                    ingredient={ingredient}
                    onClose={() => setOpen(false)}
                    onSaved={() => {
                        setOpen(false);
                        onChanged?.();
                    }}
                />
            )}
        </>
    );
}

function AssociateIngredientDialog({ ingredient, onClose, onSaved }) {
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [supplierId, setSupplierId] = useState("");
    const [price, setPrice] = useState(ingredient.cost_per_unit || 0);
    const [unit, setUnit] = useState(ingredient.unit || "kg");
    const [q, setQ] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/suppliers")
            .then((r) => setSuppliers(r.data || []))
            .catch(() => setSuppliers([]))
            .finally(() => setLoading(false));
    }, []);

    const filtered = suppliers.filter(
        (s) =>
            !q ||
            s.name.toLowerCase().includes(q.toLowerCase()) ||
            (s.city || "").toLowerCase().includes(q.toLowerCase()) ||
            (s.region || "").toLowerCase().includes(q.toLowerCase()),
    );

    const submit = async () => {
        setError("");
        if (!supplierId) {
            setError("Scegli un fornitore");
            return;
        }
        if (!(Number(price) >= 0)) {
            setError("Prezzo non valido");
            return;
        }
        setBusy(true);
        try {
            await api.post("/supplier-prices", {
                supplier_id: supplierId,
                ingredient_id: ingredient.id,
                price: Number(price),
                unit,
                notes: "Associato dalla scheda ingrediente",
            });
            const supName = suppliers.find((s) => s.id === supplierId)?.name || "fornitore";
            toast.success(`«${ingredient.name}» associato a ${supName}`);
            onSaved?.();
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/40 backdrop-blur-sm px-4"
            onClick={onClose}
            data-testid="assoc-ing-dialog"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-lg bg-paper rounded-2xl shadow-card border border-brand-line overflow-hidden max-h-[90vh] flex flex-col"
            >
                <div className="px-6 pt-5 pb-3 border-b border-brand-line shrink-0">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <div className="eyebrow text-brand-gold">Associa a fornitore</div>
                            <h3 className="font-display text-xl text-brand-ink mt-0.5 truncate">
                                {ingredient.name}
                            </h3>
                            <div className="text-[12px] text-brand-muted mt-0.5">
                                Costo attuale {euroPrecise(ingredient.cost_per_unit)} / {ingredient.unit}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="h-8 w-8 rounded-full hover:bg-brand-cream text-brand-muted flex items-center justify-center"
                            aria-label="Chiudi"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>

                <div className="px-6 py-4 space-y-3 overflow-y-auto">
                    {error && (
                        <div className="rounded-lg bg-red-50 border border-red-200 text-brand-danger text-[12px] px-3 py-2">
                            {error}
                        </div>
                    )}
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Cerca fornitore (nome, città, regione)…"
                            data-testid="assoc-search"
                            className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                        />
                    </div>

                    <div className="rounded-lg border border-brand-line bg-white max-h-56 overflow-y-auto">
                        {loading ? (
                            <div className="py-6 text-center text-sm text-brand-muted">Caricamento…</div>
                        ) : filtered.length === 0 ? (
                            <div className="py-6 text-center text-sm text-brand-muted">
                                Nessun fornitore. Vai in <strong>Fornitori → Scopri</strong> per aggiungerne.
                            </div>
                        ) : (
                            filtered.map((s) => {
                                const sel = supplierId === s.id;
                                return (
                                    <button
                                        key={s.id}
                                        type="button"
                                        onClick={() => setSupplierId(s.id)}
                                        data-testid={`assoc-pick-${s.id}`}
                                        className={`w-full px-3 py-2 flex items-center gap-2 text-left border-b border-brand-line/60 last:border-0 transition ${
                                            sel ? "bg-brand-gold/15" : "hover:bg-brand-cream/60"
                                        }`}
                                    >
                                        <div
                                            className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                                sel ? "bg-brand-navy border-brand-navy" : "border-brand-line"
                                            }`}
                                        >
                                            {sel && <Check size={11} className="text-brand-gold" strokeWidth={3} />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[13px] font-semibold text-brand-ink truncate">{s.name}</div>
                                            {(s.city || s.region) && (
                                                <div className="text-[11px] text-brand-muted truncate inline-flex items-center gap-1">
                                                    <MapPin size={10} />
                                                    {[s.city, s.region].filter(Boolean).join(", ")}
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>

                    <div className="grid grid-cols-[1fr_auto] gap-3">
                        <div>
                            <div className="eyebrow text-brand-ink/60 mb-1">Prezzo (€)</div>
                            <input
                                type="number"
                                step="0.001"
                                min="0"
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                data-testid="assoc-price"
                                className="w-full px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                            />
                        </div>
                        <div>
                            <div className="eyebrow text-brand-ink/60 mb-1">Unità</div>
                            <UnitPills value={unit} onChange={setUnit} testid="assoc-unit" />
                        </div>
                    </div>
                </div>

                <div className="px-6 py-4 bg-brand-cream flex items-center justify-end gap-2 shrink-0 border-t border-brand-line">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition"
                    >
                        Annulla
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy || !supplierId}
                        data-testid="assoc-save"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-navy text-white text-sm font-bold hover:bg-brand-navyLight transition disabled:opacity-50"
                    >
                        <Link2 size={13} /> {busy ? "Salvataggio…" : "Associa"}
                    </button>
                </div>
            </div>
        </div>
    );
}


/**
 * Pulsante "Associa ingredienti a fornitore" per le RICETTE.
 * Apre un dialog dove l'utente sceglie UN solo fornitore. Il backend
 * crea/aggiorna le voci `supplier_prices` per tutti gli ingredienti della
 * ricetta usando il `cost_per_unit` corrente come prezzo iniziale.
 */
export function AssociateRecipeToSupplierButton({ recipe, compact = false, onChanged }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button
                type="button"
                data-testid={`assoc-supplier-recipe-btn-${recipe.id}`}
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setOpen(true);
                }}
                title="Associa tutti gli ingredienti di questo piatto a un fornitore"
                className={
                    compact
                        ? "inline-flex items-center justify-center h-9 w-9 rounded-full bg-brand-navy/8 text-brand-navy hover:bg-brand-navy hover:text-white transition shrink-0"
                        : "inline-flex items-center gap-1.5 rounded-full border border-brand-navy/30 text-brand-navy hover:bg-brand-navy hover:text-white text-[12px] font-bold px-3 py-1.5 transition"
                }
            >
                <Link2 size={compact ? 15 : 13} strokeWidth={compact ? 2.2 : 2.4} />
                {!compact && <span>Associa fornitore</span>}
            </button>
            {open && (
                <AssociateRecipeDialog
                    recipe={recipe}
                    onClose={() => setOpen(false)}
                    onSaved={() => {
                        setOpen(false);
                        onChanged?.();
                    }}
                />
            )}
        </>
    );
}

function AssociateRecipeDialog({ recipe, onClose, onSaved }) {
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [supplierId, setSupplierId] = useState("");
    const [overwrite, setOverwrite] = useState(false);
    const [q, setQ] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/suppliers")
            .then((r) => setSuppliers(r.data || []))
            .catch(() => setSuppliers([]))
            .finally(() => setLoading(false));
    }, []);

    const filtered = suppliers.filter(
        (s) =>
            !q ||
            s.name.toLowerCase().includes(q.toLowerCase()) ||
            (s.city || "").toLowerCase().includes(q.toLowerCase()) ||
            (s.region || "").toLowerCase().includes(q.toLowerCase()),
    );

    const submit = async () => {
        setError("");
        if (!supplierId) {
            setError("Scegli un fornitore");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post(`/recipes/${recipe.id}/associate-supplier`, {
                supplier_id: supplierId,
                overwrite,
            });
            toast.success(
                `${data.associated} ingredient${data.associated === 1 ? "e" : "i"} associat${data.associated === 1 ? "o" : "i"} a ${data.supplier_name}` +
                    (data.skipped ? ` · ${data.skipped} già esistenti saltat${data.skipped === 1 ? "o" : "i"}` : "")
            );
            onSaved?.();
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const numIngredients = (recipe.ingredients || []).length;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/40 backdrop-blur-sm px-4"
            onClick={onClose}
            data-testid="assoc-recipe-dialog"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-lg bg-paper rounded-2xl shadow-card border border-brand-line overflow-hidden max-h-[90vh] flex flex-col"
            >
                <div className="px-6 pt-5 pb-3 border-b border-brand-line shrink-0">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <div className="eyebrow text-brand-gold">Associa ingredienti a fornitore</div>
                            <h3 className="font-display text-xl text-brand-ink mt-0.5 truncate">
                                {recipe.name}
                            </h3>
                            <div className="text-[12px] text-brand-muted mt-0.5">
                                {numIngredients} ingredient{numIngredients === 1 ? "e" : "i"} · prezzo iniziale = costo
                                corrente
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="h-8 w-8 rounded-full hover:bg-brand-cream text-brand-muted flex items-center justify-center"
                            aria-label="Chiudi"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>

                <div className="px-6 py-4 space-y-3 overflow-y-auto">
                    {error && (
                        <div className="rounded-lg bg-red-50 border border-red-200 text-brand-danger text-[12px] px-3 py-2">
                            {error}
                        </div>
                    )}
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Cerca fornitore (nome, città, regione)…"
                            data-testid="assoc-r-search"
                            className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                        />
                    </div>

                    <div className="rounded-lg border border-brand-line bg-white max-h-56 overflow-y-auto">
                        {loading ? (
                            <div className="py-6 text-center text-sm text-brand-muted">Caricamento…</div>
                        ) : filtered.length === 0 ? (
                            <div className="py-6 text-center text-sm text-brand-muted">
                                Nessun fornitore nel tuo portale.
                            </div>
                        ) : (
                            filtered.map((s) => {
                                const sel = supplierId === s.id;
                                return (
                                    <button
                                        key={s.id}
                                        type="button"
                                        onClick={() => setSupplierId(s.id)}
                                        data-testid={`assoc-r-pick-${s.id}`}
                                        className={`w-full px-3 py-2 flex items-center gap-2 text-left border-b border-brand-line/60 last:border-0 transition ${
                                            sel ? "bg-brand-gold/15" : "hover:bg-brand-cream/60"
                                        }`}
                                    >
                                        <div
                                            className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                                sel ? "bg-brand-navy border-brand-navy" : "border-brand-line"
                                            }`}
                                        >
                                            {sel && <Check size={11} className="text-brand-gold" strokeWidth={3} />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[13px] font-semibold text-brand-ink truncate">{s.name}</div>
                                            {(s.city || s.region) && (
                                                <div className="text-[11px] text-brand-muted truncate inline-flex items-center gap-1">
                                                    <MapPin size={10} />
                                                    {[s.city, s.region].filter(Boolean).join(", ")}
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>

                    <label className="inline-flex items-center gap-2 text-[12px] text-brand-ink cursor-pointer">
                        <input
                            type="checkbox"
                            checked={overwrite}
                            onChange={(e) => setOverwrite(e.target.checked)}
                            data-testid="assoc-r-overwrite"
                            className="h-3.5 w-3.5 accent-brand-navy"
                        />
                        Sovrascrivi se già a listino (predefinito: salta).
                    </label>

                    <div className="rounded-lg bg-brand-gold/10 border border-brand-gold/30 text-brand-ink/85 text-[12px] px-3 py-2">
                        I prezzi vengono inseriti con il <strong>costo corrente di ciascun ingrediente</strong>.
                        Modificali poi dal listino del fornitore. Una volta caricate le fatture, il sistema
                        intelligente sceglierà comunque automaticamente il miglior prezzo.
                    </div>
                </div>

                <div className="px-6 py-4 bg-brand-cream flex items-center justify-end gap-2 shrink-0 border-t border-brand-line">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition"
                    >
                        Annulla
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy || !supplierId}
                        data-testid="assoc-r-save"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-navy text-white text-sm font-bold hover:bg-brand-navyLight transition disabled:opacity-50"
                    >
                        <Link2 size={13} /> {busy ? "Associo…" : `Associa ${numIngredients} ingredient${numIngredients === 1 ? "e" : "i"}`}
                    </button>
                </div>
            </div>
        </div>
    );
}
